import React from "react";
import { useTranslation } from "react-i18next";
import { FormBuilder, formBuilderParams } from "components/FormBuilder/FormBuilder";
import { useWatch } from "react-hook-form";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = `${PAGE_LOCALE_KEY}employeeDetail.config.fields`;

const EmployeeKioskOption = ({ formProps, isEdit, isLoading }) => {
  const { t } = useTranslation();
  const [isKioskMode, enableTap, enableChip, tapLimit, chipLimit, swipeLimit, autoReturnCountdown] = useWatch({
    control: formProps.control,
    name: ["isKioskMode", "enableTap", "enableChip", "tapLimit", "chipLimit", "swipeLimit", "autoReturnCountdown"]
  })

  const isDisabled = !isEdit || isLoading;

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    isKioskMode: {
      fieldType: "checkbox",
      fieldName: "isKioskMode",
      label: t(`${FORM_LABEL_KEY}.isKioskMode`),
      disabled: isDisabled,
      config: {
        customOnCheckFn: (value, { setValue }) => {
          if (value) {
            setValue("enableTap", enableTap != null ? enableTap : true);
            setValue("enableChip", enableChip != null ? enableChip : true);
            setValue("tapLimit", tapLimit != null ? tapLimit : "1000");
            setValue("chipLimit", chipLimit != null ? chipLimit : "500");
            setValue("swipeLimit", swipeLimit != null ? swipeLimit : "500");
            setValue("autoReturnCountdown", autoReturnCountdown != null ? autoReturnCountdown : "5");
          }
        }
      }
    },

    enableCustomWallpaper: {
      fieldType: "checkbox",
      fieldName: "enableCustomWallpaper",
      label: t(`${FORM_LABEL_KEY}.enableCustomWallpaper`),
      disabled: isDisabled,
    },

    enableTap: {
      fieldType: "checkbox",
      fieldName: "enableTap",
      label: t(`${FORM_LABEL_KEY}.enableTap`),
      disabled: isDisabled,
    },
    enableChip: {
      fieldType: "checkbox",
      fieldName: "enableChip",
      label: t(`${FORM_LABEL_KEY}.enableChip`),
      disabled: isDisabled,
    },
    enableSwipe: {
      fieldType: "checkbox",
      fieldName: "enableSwipe",
      label: t(`${FORM_LABEL_KEY}.enableSwipe`),
      disabled: isDisabled,
    },

    tapLimit: {
      fieldType: "textInput",
      fieldName: "tapLimit",
      label: t(`${FORM_LABEL_KEY}.tapLimit`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "onlyMaxEightDigits"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    chipLimit: {
      fieldType: "textInput",
      fieldName: "chipLimit",
      label: t(`${FORM_LABEL_KEY}.chipLimit`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "onlyMaxEightDigits"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    swipeLimit: {
      fieldType: "textInput",
      fieldName: "swipeLimit",
      label: t(`${FORM_LABEL_KEY}.swipeLimit`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "onlyMaxEightDigits"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    autoReturnCountdown: {
      fieldType: "textInput",
      fieldName: "autoReturnCountdown",
      label: t(`${FORM_LABEL_KEY}.autoReturnCountdown`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyNonZeroInteger", "onlyMaxEightDigits"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
  }

  return (
    <FormBuilder.Section
      title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.kioskOption`)}
      titleVariant="h3"
      padding={formBuilderParams.formSectionLeftPadding}>
      <FormBuilder.Row itemsPerRow={3}>
        {FormBuilder.buildFields({
          formProps, fields: [
            fieldsConfig.isKioskMode
          ]
        })}
      </FormBuilder.Row>
      {
        isKioskMode ?
          <>
            <FormBuilder.Section
              title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.config`)}
              titleVariant="h4">
              <FormBuilder.Row itemsPerRow={3}>
                {FormBuilder.buildFields({
                  formProps, fields: [
                    fieldsConfig.enableCustomWallpaper,
                  ]
                })}
              </FormBuilder.Row>
            </FormBuilder.Section>
            <FormBuilder.Section
              title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.entryMode`)}
              titleVariant="h4">
              <FormBuilder.Row itemsPerRow={3}>
                {FormBuilder.buildFields({
                  formProps, fields: [
                    fieldsConfig.enableTap,
                    fieldsConfig.enableChip,
                    fieldsConfig.enableSwipe
                  ]
                })}
              </FormBuilder.Row>
            </FormBuilder.Section>
            <FormBuilder.Section
              title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.transactionLimit`)}
              titleVariant="h4">
              <FormBuilder.Row itemsPerRow={3}>
                {FormBuilder.buildFields({
                  formProps, fields: [
                    fieldsConfig.tapLimit,
                    fieldsConfig.chipLimit,
                    fieldsConfig.swipeLimit,
                    fieldsConfig.autoReturnCountdown
                  ]
                })}
              </FormBuilder.Row>
            </FormBuilder.Section>
          </> : null
      }
    </FormBuilder.Section>
  );
};

export default React.memo(EmployeeKioskOption);
